// /receiving/item/:id — one item in a receipt box.
//
// The box (receipt_boxes) carries the sticker; the item (receipt_items) is the
// stock row inside it. Transactions, boards and instances hang off the ITEM.
// Reprinting is a box action, so it targets the box id, never the item id:
// the two differ on every receive after migration 102.

import React, { useCallback, useEffect, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Tag } from '../components/ui/Tag';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Link, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import HelpDrawer from '../components/HelpDrawer';
import { DataTable } from '../components/table/DataTable';
import ItemName, { itemLabel, type ItemNameSource } from '../components/ItemName';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { formatTimestamp, formatEventDate } from '../utils/date-utils';
import InventoryAdjustAction from '../Parts/InventoryAdjustAction';
import BoxLocation from './BoxLocation';
import { openAuthedPdf } from './openAuthedPdf';
import { printBoxInstanceQRLabels } from '../utils/printing-utils';
import PageLayout from '../components/ui/PageLayout';

/**
 * Does this description only repeat what the item is already called?
 *
 * Three forms to compare against, not one: the heading prints `base_name` with
 * `variant_detail` beside it, `itemLabel` returns the two joined, and a row with
 * neither falls back to the description itself. A check against only one of them
 * prints "Mounting bracket" under "Mounting bracket (rev A)".
 */
function namesTheSameThing(text: string, item: ItemNameSource): boolean {
  const t = text.trim().toLowerCase();
  return [item.base_name, item.display_name, itemLabel(item), item.sku].some(
    (n) => typeof n === 'string' && n.trim().toLowerCase() === t
  );
}

interface ItemDetail {
  item: {
    id: string;
    receipt_box_id: string;
    expected_receipt_id: string | null;
    part_catalog_id: number;
    mpn: string | null;
    revision: string | null;
    manufacture_date: string | null;
    lot_number: string | null;
    vendor: string | null;
    purchase_order_ref: string | null;
    procurement_receipt_id: string | null;
    quantity_received: number;
    quantity_remaining: number;
    /** 037. A 'rejected' item is real stock at QC Hold, not stock you can build from. */
    disposition?: string;
    split_from_item_id?: string | null;
    unit_cost_cents: number | null;
    received_at: string;
    received_by: string | null;
    /** Joined display name for received_by. Null when the user row is gone. */
    received_by_name: string | null;
    location_id: number | null;
    notes: string | null;
    sku: string | null;
    part_description: string | null;
    /** From catalog_item_names, joined on part_catalog_id. base_name is the
     *  thing, variant_detail is what distinguishes this one from its siblings,
     *  display_name is the two composed. */
    base_name?: string | null;
    display_name?: string | null;
    variant_detail?: string | null;
    tracked_per_instance: boolean;
    location_name: string | null;
    section_code?: string | null;
    area_name?: string | null;
    warehouse_name?: string | null;
    location_path?: string;
  };
  /** The box this item is in. Its barcode is the sticker. */
  box: {
    id: string;
    barcode: string;
    box_no: number | null;
    box_count: number | null;
    delivery_id: string | null;
    /** The delivery's order, off the box. An item received through the order
     *  page carries no typed ref of its own, so this is where its order lives. */
    po_number?: string | null;
  };
  transactions: Array<{
    id: number | string;
    transaction_type: 'receive' | 'consume' | 'transfer' | 'adjust';
    quantity_delta: number;
    occurred_at: string;
    performed_by: string | null;
    /** Joined display name for performed_by. */
    performed_by_name: string | null;
    note: string | null;
    from_location_id: number | null;
    to_location_id: number | null;
  }>;
  boards: Array<{
    id: number | string;
    primary_uid: string | null;
    serialnumber: string | null;
    is_current: boolean;
    retired_at: string | null;
  }>;
  instances: Array<{
    id: number | string;
    serial: string;
    status: string;
    installed_on: string | null;
    installed_at: string | null;
    retired_at: string | null;
  }>;
}

// One shape each, taken off the response interface rather than restated: the
// three tables below need named row types and the server's contract is already
// written once, above.
type ItemTransaction = ItemDetail['transactions'][number];
type LinkedBoard = ItemDetail['boards'][number];
type LinkedInstance = ItemDetail['instances'][number];

const formatCurrency = (cents: number | null) =>
  cents === null || cents === undefined ? '—' : `$${(cents / 100).toFixed(2)}`;

const fieldLabelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

// ---------------------------------------------------------------------------
// The three read-only tables on this page, as shared-DataTable columns.
//
// Module scope, per the DataTable contract: every cell is pure over its row,
// nothing closes over page state, and the arrays never change identity. No
// `sortable` and no accessorFn anywhere — all three are HISTORIES, read in the
// order the server sends them, and a re-sorted history is a different claim
// about what happened to the item.
//
// Alignment and tabular figures come from `meta`, never from a class string,
// and the vessel owns the td: a color that varies per row rides a span inside
// the cell instead.
// ---------------------------------------------------------------------------

const TRANSACTION_COLUMNS: ColumnDef<ItemTransaction, any>[] = [
  {
    id: 'when',
    header: 'When',
    meta: { numeric: true, cellClass: 'text-on-surface whitespace-nowrap' },
    cell: ({ row }) => formatTimestamp(row.original.occurred_at, 'MMM D, YYYY h:mm A'),
  },
  {
    id: 'type',
    header: 'Type',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.transaction_type,
  },
  {
    id: 'delta',
    header: 'Δ Qty',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => (
      <span className={row.original.quantity_delta < 0 ? 'text-red-300' : 'text-green-300'}>
        {row.original.quantity_delta > 0 ? '+' : ''}
        {row.original.quantity_delta}
      </span>
    ),
  },
  {
    // Per MOVEMENT, not per item. It differs from the item's own Received by the
    // moment somebody corrects or adjusts against another person's receive,
    // which is exactly when the distinction earns its column.
    id: 'by',
    header: 'By',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.performed_by_name || '—',
  },
  {
    id: 'note',
    header: 'Note',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.note || '—',
  },
];

const BOARD_COLUMNS: ColumnDef<LinkedBoard, any>[] = [
  {
    id: 'primary_uid',
    header: 'Primary UID',
    meta: { cellClass: 'font-mono text-xs text-on-surface' },
    cell: ({ row }) => row.original.primary_uid || '—',
  },
  {
    id: 'serial',
    header: 'Serial',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.serialnumber || '—',
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center' },
    cell: ({ row }) =>
      row.original.is_current ? (
        <Tag tone="good">current</Tag>
      ) : (
        <Tag>retired</Tag>
      ),
  },
];

const INSTANCE_COLUMNS: ColumnDef<LinkedInstance, any>[] = [
  {
    id: 'serial',
    header: 'Serial',
    meta: { cellClass: 'font-mono text-xs text-primary' },
    cell: ({ row }) => row.original.serial,
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center', cellClass: 'text-on-surface capitalize' },
    cell: ({ row }) => row.original.status,
  },
  {
    id: 'installed_on',
    header: 'Installed on',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.installed_on || '—',
  },
  {
    id: 'installed_at',
    header: 'Installed at',
    meta: { numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) =>
      row.original.installed_at
        ? formatTimestamp(row.original.installed_at, 'MMM D, YYYY')
        : '—',
  },
];

const ReceiptItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  // Strict: cost visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = useUserStore((s) => !!s.user.can_financial);
  // `procurement` only, matching POST /api/procurement/returns. Deliberately
  // NARROWER than this page's own gate: reading an item is a receiving job, and
  // opening a claim against a supplier is a purchasing one.
  const canReject = useUserStore(
    (s) => !!(s.user.isadmin || s.user.can_admin || s.user.can_procurement)
  );

  const [detail, setDetail] = useState<ItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(false);

  // ── THE LATE REJECTION (037) ─────────────────────────────────────────────
  //
  // Mannie, on the morning 037 was designed: "defects are often not seen until
  // after the box is received, and the parts are then taken out to flash, for
  // example, later down the line."
  //
  // That is the COMMON case, and it shipped with an API and nowhere to click.
  // /procurement/returns is a worklist — it lists what is owed and writes one
  // off — and nothing anywhere raised a return against a box already on a shelf.
  //
  // It belongs HERE because of where the person is standing: they scanned the
  // sticker on the box they are holding. Collapsed by default for the same
  // reason the receive drawer's is — almost every box is fine, and a form sitting
  // open beside a good box is a thing to scroll past a hundred times.
  const [rejecting, setRejecting] = useState(false);
  const [rejectQty, setRejectQty] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectResolution, setRejectResolution] = useState<'return_to_vendor' | 'scrap'>('return_to_vendor');
  const [rejectBusy, setRejectBusy] = useState(false);

  // ── THE COUNT WAS WRONG (the 108) ────────────────────────────────────────
  //
  // A box received as 108 when it held 20 put 88 units of stock on the shelf that
  // did not exist and closed a purchase-order line with 88 still owed. Nothing in
  // the app could fix either: Adjust Remaining moves the location rollup only, and
  // a return says the goods went back to the vendor, which is a different and
  // untrue claim.
  //
  // COLLAPSED, and lower down the page than Adjust, for the same reason the
  // rejection panel is: almost every box is keyed correctly, and this is the
  // action with the widest reach on the page. ADMIN ONLY, matching the endpoint.
  const canCorrect = useUserStore((s) => !!(s.user.isadmin || s.user.can_admin));
  const [correcting, setCorrecting] = useState(false);
  const [correctQty, setCorrectQty] = useState('');
  const [correctReason, setCorrectReason] = useState('');
  const [correctBusy, setCorrectBusy] = useState(false);
  /**
   * Set when the server refuses an UPWARD correction for landing past the
   * over-receipt allowance. Holds the server's own numbers so the confirmation
   * states real quantities.
   *
   * Without this the panel was a DEAD END for a whole class of fix, and the common
   * one: `DEFAULT_OVER_RECEIPT_PCT` is 0, so any correction that lands past
   * quantity_expected is refused. A box keyed 38 that really held 42 on a line of
   * 40 got a toast reading "approving the extra 2 is a separate decision" and no
   * way anywhere in the app to make that decision, so the box stayed wrong
   * permanently. The receive drawer has carried the same dialog for its own path
   * since the over-receipt gate shipped.
   *
   * Only reachable by an admin, because the whole panel is.
   */
  const [correctOverReceipt, setCorrectOverReceipt] = useState<{
    expected: number;
    already_received: number;
    this_receive: number;
    total_after: number;
  } | null>(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await api.get(`/receipt-items/${id}`)) as ItemDetail;
      setDetail(data);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load this item. Reload to try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) return <LogoSpinner label="Loading the item" />;
  if (!detail) {
    return (
      <PageLayout kind="record" className="pt-10">
        <EmptyState
          title="This item could not be loaded."
          action={<BackLink to="/receiving" label="Receiving" />}
        >
          It may have been removed, or the link is wrong.
        </EmptyState>
      </PageLayout>
    );
  }
  const { item, box, transactions, boards: linked_boards, instances: linked_instances } = detail;

  // Null while the typed value is not yet a usable number, so the mirror below
  // does not flash 'takes NaN out of stock' as somebody types.
  const parsedCorrectQty = parseInt(correctQty, 10);
  const correctDelta =
    Number.isInteger(parsedCorrectQty) && parsedCorrectQty > 0 && parsedCorrectQty !== item.quantity_received
      ? parsedCorrectQty - item.quantity_received
      : null;

  const handleReprint = async () => {
    try {
      // /reprint-label is POST and returns JSON pointing at the rendered PDF.
      await openAuthedPdf(`/api/receipt-boxes/${box.id}/reprint-label`, {
        method: 'POST',
        followJsonRedirect: true,
      });
      toast.success(`Reprinted the sticker for ${box.barcode}`);
    } catch (err: any) {
      toast.error(err?.message || 'Reprint failed');
    }
  };

  const handlePrintQrs = async () => {
    if (!item.tracked_per_instance || linked_instances.length === 0) return;
    try {
      // Rendered locally onto the drone-sticker roll stock, no server round-trip.
      // The bookend tiles name the ITEM, because a box can hold several.
      await printBoxInstanceQRLabels(linked_instances, {
        sku: itemLabel(item),
        barcode: box.barcode,
      });
    } catch (err: any) {
      toast.error(err?.message || 'QR sheet failed');
    }
  };

  /**
   * Raise a return against this item.
   *
   * The LATE path: these units were accepted into stock and turned out to be bad
   * afterwards. The server moves them out of wherever they are and into QC Hold
   * (or straight to Scrap), writes a `transfer`, and opens the return. It does NOT
   * touch the purchase order line's received quantity: the vendor did ship them,
   * and what went back comes off what the line is still expecting instead, so a
   * replacement lands against the same line rather than reading as an
   * over-receipt. Nobody is asked here whether money is coming back, because at
   * rejection time nobody knows: the vendor answers that on the Returns page.
   */
  /**
   * Correct the quantity this item was keyed with.
   *
   * The server does every real check (the consumed floor, tracked-instance
   * removability, the over-receipt allowance when correcting upwards) and this
   * repeats only the two that are cheap and would otherwise cost a round trip to
   * learn. Everything else surfaces as the server's own sentence, which is
   * written for the person reading it.
   */
  const handleCorrect = async () => {
    const qty = parseInt(correctQty, 10);
    if (!Number.isInteger(qty) || qty <= 0) {
      toast.error('The real count has to be a whole number above zero.');
      return;
    }
    if (qty === item.quantity_received) {
      toast.error('That is already the quantity on this item.');
      return;
    }
    if (!correctReason.trim()) {
      toast.error('Say why the number was wrong. It is the only thing that will explain this later.');
      return;
    }
    await submitCorrection(qty, false);
  };

  /**
   * The POST, separated from the validation above so the over-receipt confirmation
   * can re-send the identical correction with the approval attached rather than
   * asking the person to retype it.
   */
  const submitCorrection = async (qty: number, approveOverReceipt: boolean) => {
    setCorrectBusy(true);
    try {
      const res = (await api.post(`/receipt-items/${item.id}/correct-quantity`, {
        body: {
          quantity_received: qty,
          reason: correctReason.trim(),
          ...(approveOverReceipt ? { approve_over_receipt: true } : {}),
        },
      })) as { delta: number; line_status: string | null; instances_removed: number };
      toast.success(
        `Corrected to ${qty}.`
        + (res.line_status === 'partial'
          ? ' The order line is open again for the balance.'
          : '')
      );
      setCorrecting(false);
      setCorrectQty('');
      setCorrectReason('');
      setCorrectOverReceipt(null);
      // The item, its transaction list, its instances and the header card all moved.
      fetchDetail();
    } catch (err: any) {
      // More than the order allows. Same envelope as the receive path, so the
      // numbers are the server's rather than ours. Deliberately not a toast: this
      // needs reading, and a toast disappears while somebody is reading it.
      if (err?.status === 409 && err?.data?.error?.code === 'over_receipt_needs_approval') {
        const d = err.data.error;
        setCorrectOverReceipt({
          expected: d.expected,
          already_received: d.already_received,
          this_receive: d.this_receive,
          total_after: d.total_after,
        });
        return;
      }
      toast.error(err?.data?.error?.message || err?.message || 'That correction could not be made');
    } finally {
      setCorrectBusy(false);
    }
  };

  const handleReject = async () => {
    const qty = parseInt(rejectQty, 10);
    if (!Number.isInteger(qty) || qty <= 0) {
      toast.error('How many are bad? A whole number above zero.');
      return;
    }
    if (qty > item.quantity_remaining) {
      toast.error(
        `Only ${item.quantity_remaining} of this item ${item.quantity_remaining === 1 ? 'is' : 'are'} still here. `
        + 'The rest has already been used or moved, so it cannot be pulled back.'
      );
      return;
    }
    if (!rejectReason.trim()) {
      toast.error('Say what is wrong with them. The vendor conversation and the credit note both need it.');
      return;
    }
    setRejectBusy(true);
    try {
      await api.post('/procurement/returns', {
        body: {
          receipt_item_id: item.id,
          quantity: qty,
          reason: rejectReason.trim(),
          resolution: rejectResolution,
        },
      });
      toast.success(
        rejectResolution === 'scrap'
          ? `${qty} written off. They are at Scrap and out of what you can build.`
          : `${qty} moved to QC Hold. The return is open under Returns.`
      );
      setRejecting(false);
      setRejectQty('');
      setRejectReason('');
      // The item's remaining quantity and its transaction list both moved.
      fetchDetail();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That return could not be raised');
    } finally {
      setRejectBusy(false);
    }
  };

  return (
    <PageLayout kind="record" className="pt-4" header={(
      <div className="mb-5">
        <BackLink to="/receiving" label="Receiving" />
        <div className="mt-1 flex items-start gap-2">
        {/* A DETAIL PAGE, so the SKU is allowed: this is where somebody who
            scanned a sticker comes to look the code up, and where it is printed
            on the label they are holding. The name still leads. The revision has
            no span of its own because variant_detail already carries it. */}
          <h1 className="min-w-0 text-left">
            <ItemName size="lg" showSku item={item} />
          </h1>
          <HelpDrawer title="About this item">
            <p>
              A box is the physical thing with the sticker on it. An item is what is
              inside: one part, how much of it arrived, how much is left, and everything
              that has happened to it. Most boxes hold one item, and scanning their
              sticker lands here.
            </p>
            <p className="text-on-surface font-medium">Received versus filed</p>
            <p>
              <span className="text-on-surface">Received</span> is when the goods actually
              arrived. <span className="text-on-surface">Filed</span> is when somebody keyed
              it into the system. They are usually the same day. When they are not, an
              admin backdated the arrival so it lands in the right month for the books, and
              the row is marked so a typed date is never mistaken for one the system
              watched happen.
            </p>
            <p className="text-on-surface font-medium">Quantity remaining</p>
            <p>
              Counts down as stock is consumed out of this item, which is how a part can
              be traced back to the delivery and the lot it came from. An item at zero
              stays on record: it is history, not clutter.
            </p>
            <p className="text-on-surface font-medium">Reprinting the box sticker</p>
            <p>
              The sticker belongs to the box, so a reprint covers everything in it. It
              regenerates from the current catalog record, so it always shows the
              part&apos;s CURRENT name. If the SKU was renamed, the old sticker still
              scans and still finds this box, so there is no need to relabel anything
              already on the shelf.
            </p>
          </HelpDrawer>
        </div>
        <p className="mt-1 text-sm text-on-surface-variant">
          {item.disposition === 'rejected' ? 'Held in QC box' : 'In box'}{' '}
          <Link
            to={`/receiving/box/${box.id}`}
            className="font-mono text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
          >
            {box.barcode}
          </Link>
          {item.disposition !== 'rejected' && box.box_no != null && box.box_count != null && (
            <span className="tabular-nums">, {box.box_no} of {box.box_count}</span>
          )}
        </p>
        {/* Only when it says something the name does not. On most rows the name
            IS the description (catalog_item_names falls back to it), and printing
            both is one line of nothing. Checked against every form of the name,
            because base_name and display_name differ by the variant detail. */}
        {item.part_description && !namesTheSameThing(item.part_description, item) && (
          <p className="text-sm text-on-surface-variant mt-1">{item.part_description}</p>
        )}
        {/* SAID AT THE TOP, not buried in the field grid. Somebody who just
            scanned this sticker looking for stock needs to know before anything
            else that this item is the rejected half of a delivery — it is real, it
            is findable, and it counts toward nothing you can build. */}
        {item.disposition === 'rejected' && (
          <Alert tone="warning" variant="note" title="Not acceptable." className="mt-2">
            These were rejected and are held out of stock. What is owed for them is under{' '}
            <Link to="/procurement/returns" className="underline hover:text-on-surface">
              Returns
            </Link>
            .
          </Alert>
        )}
      </div>
    )}>
    <div className="space-y-5">

      {/* Header card */}
      <Panel label="Receipt details">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Fact label="Received" value={formatTimestamp(item.received_at, 'MMM D, YYYY h:mm A')} />
        {/* WHO FILED THIS ITEM. The uuid has always been on this response and
            no screen ever showed a name, so "who received 108 of these" was a
            question only a database query could answer. Next to Received
            because when and by whom are one fact. */}
        <Fact label="Received by" value={item.received_by_name || '—'} />
        <Fact label="Location" value={<BoxLocation where={item} />} />
        <Fact
          label="Quantity"
          value={
            <>
              <span className="tabular-nums">{item.quantity_remaining}</span>
              <span className="text-on-surface-variant"> / {item.quantity_received}</span>
            </>
          }
        />
        {canSeeFinancials && (
          <Fact label="Unit cost" value={formatCurrency(item.unit_cost_cents)} />
        )}
        <Fact label="Vendor" value={item.vendor || '—'} />
        <Fact label="Purchase order" value={item.purchase_order_ref || box.po_number || '—'} />
        <Fact label="Lot number" value={item.lot_number || '—'} />
        <Fact
          label="Manufacture date"
          value={item.manufacture_date ? formatEventDate(item.manufacture_date, 'MMM D, YYYY') : '—'}
        />
      </div>
      </Panel>

      {/* Some of these turned out to be bad (037) */}
      {item.quantity_remaining > 0 && item.disposition !== 'rejected' && (
        canReject ? (
          !rejecting ? (
            <button
              type="button"
              onClick={() => setRejecting(true)}
              className="text-xs text-on-surface-variant hover:text-primary underline underline-offset-2 self-start
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
            >
              Some of these turned out to be bad
            </button>
          ) : (
            <Panel
              level={3}
              title="Not acceptable"
              className="space-y-3 ring-1 ring-amber-500/30"
              actions={(
                <button
                  type="button"
                  className={`${buttonClass('quiet', 'sm')} shrink-0`}
                  onClick={() => { setRejecting(false); setRejectQty(''); setRejectReason(''); }}
                >
                  Cancel
                </button>
              )}
            >
              <p className="text-[11px] text-on-surface-variant leading-relaxed">
                These were received, so the vendor still shipped them. They move out of
                stock to QC Hold and a return opens. The purchase order keeps its received
                quantity: the return is what claws it back, and only if you expect money.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* The hint is OUTSIDE the label, deliberately. Text inside a
                    wrapping label becomes part of the field's accessible name, so
                    a screen reader would announce this input as "How many are
                    bad34 of this item are still here. Anything already used cannot
                    be pulled back." The hint is guidance, not the field's name. */}
                <div className="block">
                  <label className="block">
                    <span className={fieldLabelClass}>How many are bad</span>
                    <Field
                      thumb
                      type="number"
                      min={1}
                      max={item.quantity_remaining}
                      value={rejectQty}
                      onChange={(e) => setRejectQty(e.target.value)}
                      placeholder="0"
                    />
                  </label>
                  <p className="mt-1 text-[11px] text-on-surface-variant leading-snug">
                    {item.quantity_remaining} of this item {item.quantity_remaining === 1 ? 'is' : 'are'}{' '}
                    still here. Anything already used cannot be pulled back.
                  </p>
                </div>
                <label className="block">
                  <span className={fieldLabelClass}>What is wrong with them</span>
                  <Field
                    thumb
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Dead at the flash station, bent pins"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={fieldLabelClass}>What happens to them</span>
                  <Select
                    label="What happens to them"
                    value={rejectResolution}
                    onChange={(key) => setRejectResolution(key as 'return_to_vendor' | 'scrap')}
                    options={[
                      { key: 'return_to_vendor', label: 'Send back to the vendor' },
                      { key: 'scrap', label: 'Scrap them' },
                    ]}
                  />
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={rejectBusy}
                  className={buttonClass('filled', 'md')}
                >
                  {rejectBusy ? 'Raising…' : 'Raise the return'}
                </button>
              </div>
            </Panel>
          )
        ) : null
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleReprint}
          className={buttonClass('tonal', 'md')}
        >
          Reprint box sticker
        </button>
        {item.tracked_per_instance && linked_instances.length > 0 && (
          <button
            type="button"
            onClick={handlePrintQrs}
            className={buttonClass('tonal', 'md')}
          >
            Print Item QRs ({linked_instances.length})
          </button>
        )}
        <button
          type="button"
          onClick={() => setAdjustOpen(true)}
          className={buttonClass('warn', 'md')}
        >
          Adjust Remaining
        </button>
      </div>

      {item.notes && (
        <Panel label="Notes" padding="sm" className="text-sm text-on-surface-variant">
          <div>
            <span className="text-xs uppercase tracking-wider text-on-surface-variant mr-2">Notes:</span>
            {item.notes}
          </div>
        </Panel>
      )}

      {/* The count on this item was wrong */}
      {canCorrect && (
        !correcting ? (
          <FadeIn key="keyed-wrong">
          <button
            type="button"
            onClick={() => {
              setCorrecting(true);
              setCorrectQty('');
            }}
            className="text-xs text-on-surface-variant hover:text-primary underline underline-offset-2 self-start
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
          >
            This item was keyed with the wrong count
          </button>
          </FadeIn>
        ) : (
          <FadeIn key="correcting">
          <Panel
            level={3}
            title="Correct the count"
            className="space-y-3 ring-1 ring-red-500/30"
            actions={(
              <button
                type="button"
                className={`${buttonClass('quiet', 'sm')} shrink-0`}
                onClick={() => {
                  setCorrecting(false);
                  setCorrectQty('');
                  setCorrectReason('');
                }}
              >
                Cancel
              </button>
            )}
          >
            <p className="text-[11px] text-on-surface-variant leading-relaxed">
              For a number that was typed wrong, not for parts that arrived and then went
              somewhere. The received entry stays on the record and a correction is logged
              beside it, so this item will show both.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
                  How many were really in it
                </span>
                <Field
                  thumb
                  type="number"
                  min={1}
                  value={correctQty}
                  onChange={(e) => setCorrectQty(e.target.value)}
                  placeholder={`Currently says ${item.quantity_received}`}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
                  Why was it wrong
                </span>
                <Field
                  thumb
                  type="text"
                  value={correctReason}
                  onChange={(e) => setCorrectReason(e.target.value)}
                  placeholder="Counted the order balance, not the box"
                />
              </label>
            </div>

            {/* THE SAME MIRROR AS THE RECEIVE DRAWER, for the same reason: say
                what the number does before it is pressed, not after. */}
            <p className="text-[11px] leading-snug min-h-[1rem]" role="status" aria-live="polite">
              {correctDelta === null ? (
                <span className="text-on-surface-variant">Type the real count to see what this changes.</span>
              ) : (
                <span className="text-on-surface-variant">
                  Takes{' '}
                  <span className="tabular-nums text-on-surface">
                    {correctDelta > 0 ? '+' : ''}{correctDelta}
                  </span>{' '}
                  {correctDelta > 0 ? 'into' : 'out of'} stock at{' '}
                  {item.location_name || 'this location'}
                  {item.expected_receipt_id
                    ? ', and moves the purchase order line by the same amount.'
                    : '.'}
                  {item.tracked_per_instance && linked_instances.length > 0 && correctDelta < 0 && (
                    <>
                      {' '}
                      <span className="text-amber-300">
                        Removes {Math.abs(correctDelta)} of its item records, if they are still
                        untouched.
                      </span>
                    </>
                  )}
                </span>
              )}
            </p>

            <Reveal show={!!correctOverReceipt}>
            {correctOverReceipt && (
              <Alert
                tone="warning"
                title="That is more than the order asked for"
                action={(
                  <>
                    <button
                      type="button"
                      className={buttonClass('quiet', 'sm')}
                      onClick={() => setCorrectOverReceipt(null)}
                    >
                      Leave it alone
                    </button>
                    <button
                      type="button"
                      disabled={correctBusy}
                      // The SAME number, re-sent verbatim with the approval attached.
                      // It is still in the field; deriving it back out of the server's
                      // totals would be arithmetic with nothing to gain.
                      onClick={() => submitCorrection(parseInt(correctQty, 10), true)}
                      className={buttonClass('warn', 'sm')}
                    >
                      {correctBusy ? 'Correcting…' : 'Approve the excess and correct'}
                    </button>
                  </>
                )}
              >
                <span className="tabular-nums">
                  The line expects{' '}
                  <span className="text-on-surface">{correctOverReceipt.expected}</span> and has{' '}
                  <span className="text-on-surface">{correctOverReceipt.already_received}</span>{' '}
                  against it. This correction takes it to{' '}
                  <span className="text-on-surface">{correctOverReceipt.total_after}</span>, which is{' '}
                  <span className="text-on-surface">
                    {correctOverReceipt.total_after - correctOverReceipt.expected}
                  </span>{' '}
                  over. Correcting the count and accepting the excess are two decisions, so this
                  one is yours to make out loud.
                </span>
              </Alert>
            )}
            </Reveal>

            <div className="flex justify-end">
              <button
                type="button"
                disabled={correctBusy || !!correctOverReceipt}
                onClick={handleCorrect}
                className={buttonClass('danger', 'md')}
              >
                {correctBusy ? 'Correcting…' : 'Correct the count'}
              </button>
            </div>
          </Panel>
          </FadeIn>
        )
      )}

      {/* Transactions.
          The bare <section> is the landmark only: the vessel is the visual
          shell, and emptyMode 'replace' keeps today's behavior of showing the
          message INSTEAD of an empty grid with column names over nothing. */}
      <section aria-label="Inventory Transactions">
        <DataTable<ItemTransaction>
          data={transactions}
          columns={TRANSACTION_COLUMNS}
          getRowId={(tx) => String(tx.id)}
          strip={<h2 className="text-sm font-semibold text-on-surface">Inventory Transactions</h2>}
          empty={<Empty label="No transactions yet." />}
          emptyMode="replace"
        />
      </section>

      {/* Linked boards (AP/ESC if first-flash came from this item) */}
      <section aria-label={`Linked Boards (${linked_boards.length})`}>
        <DataTable<LinkedBoard>
          data={linked_boards}
          columns={BOARD_COLUMNS}
          getRowId={(b) => String(b.id)}
          strip={
            <h2 className="text-sm font-semibold text-on-surface">
              Linked Boards ({linked_boards.length})
            </h2>
          }
          empty={<Empty label="No boards flashed from this item yet." />}
          emptyMode="replace"
        />
      </section>

      {/* Per-instance non-flashable items */}
      {item.tracked_per_instance && (
        <section aria-label={`Item Instances (${linked_instances.length})`}>
          <DataTable<LinkedInstance>
            data={linked_instances}
            columns={INSTANCE_COLUMNS}
            getRowId={(inst) => String(inst.id)}
            strip={
              <h2 className="text-sm font-semibold text-on-surface">
                Item Instances ({linked_instances.length})
              </h2>
            }
            empty={<Empty label="No instances created. Receive flow should have inserted these." />}
            emptyMode="replace"
          />
        </section>
      )}

      {adjustOpen && (
        <InventoryAdjustAction
          partCatalogId={item.part_catalog_id}
          sku={item.sku || ''}
          locationId={item.location_id}
          locationName={item.location_name}
          onClose={() => setAdjustOpen(false)}
          onAdjusted={() => {
            setAdjustOpen(false);
            fetchDetail();
          }}
        />
      )}
    </div>
    </PageLayout>
  );
};

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-on-surface-variant mb-0.5">{label}</div>
    <div className="text-sm text-on-surface">{value}</div>
  </div>
);

// The three tables' empty message. Nothing but a self-padded, self-colored
// node: the DataTable renders it in the field IN PLACE OF the table, so this is
// the whole of what an empty section says.
const Empty: React.FC<{ label: string }> = ({ label }) => (
  <EmptyState variant="area">{label}</EmptyState>
);

export default ReceiptItemDetail;
